import type { Router } from 'vue-router'

// Every route component is loaded on demand, so a deploy that replaces the
// content-hashed chunks pulls them out from under any tab still running the
// previous build: the next trip to a screen that tab has not visited yet fails
// its import, and the navigation dies where the user can only see that nothing
// happened. Browsers word that rejection differently and none of them gives it
// a type, a code, or a stack to match on, so the message text is all there is.
const STALE_BUILD_MESSAGES = [
  'failed to fetch dynamically imported module', // Chrome, Edge
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari
  'unable to preload css' // Vite's own preload helper
]

// Session storage rather than a variable, because the flag has to survive the
// very reload it triggers — that is how the fresh build knows it is the retry
// and must not reload again. Pendo is allowed to frame this app, and storage
// access throws outright in a framed document whose site data is blocked, so
// every read and write of it has to tolerate failing.
const RETRY_KEY = 'nomic.stale-build-retry'

export function isStaleBuildError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return STALE_BUILD_MESSAGES.some((known) => message.toLowerCase().includes(known))
}

function readRetryTarget(): string | null {
  try {
    return sessionStorage.getItem(RETRY_KEY)
  } catch {
    return null
  }
}

function writeRetryTarget(target: string | null) {
  try {
    if (target === null) {
      sessionStorage.removeItem(RETRY_KEY)
    } else {
      sessionStorage.setItem(RETRY_KEY, target)
    }
  } catch {
    // Without storage the retry flag cannot be kept, which costs the loop
    // guard across the reload boundary but never blocks the recovery itself.
  }
}

/**
 * Recovers a tab left on a build the origin no longer serves: a navigation
 * that fails because its chunk is gone reloads the app straight onto the
 * address it was heading for, so the user lands where they were going instead
 * of staying on the previous screen with nothing to explain it.
 *
 * Failures of any other kind are left alone — they are the router's to report,
 * and reloading on them would hide them behind a flicker.
 */
export function setStaleBuildReloadGuard(router: Router) {
  let reloading = false

  router.onError((error, to) => {
    if (!isStaleBuildError(error)) return

    // Either a reload is already on its way, or this address is the one the
    // last reload was for and it has just failed again — which means the
    // chunk is missing for some reason a fresh build will not fix. Reloading
    // past that point only loops.
    if (reloading || readRetryTarget() === to.fullPath) return

    reloading = true
    writeRetryTarget(to.fullPath)
    window.location.href = router.resolve(to.fullPath).href
  })

  router.afterEach((to, from, failure) => {
    // Arriving anywhere proves the build being served loads, so the flag has
    // done its job; leaving it set would make the tab refuse to recover from
    // the next deploy. Once a reload is ordered, though, nothing this page
    // does in the moment it has left may clear the flag out from under it.
    if (!failure && !reloading) writeRetryTarget(null)
  })
}
